import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface ClaimConfirmDialogProps {
  /** The trigger element – usually the visible Claim button. */
  trigger: React.ReactNode;
  onConfirm?: () => Promise<void>;
  title?: string;
  description?: string;
  confirmLabel?: string;
  actionName?: string;
  failureMessage?: string;
  conflictMessage?: string;
}

export function ClaimConfirmDialog({
  trigger,
  onConfirm,
  title,
  description,
  confirmLabel,
  actionName = "claim",
  failureMessage,
  conflictMessage,
}: ClaimConfirmDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultFailureMessage = failureMessage ?? `Failed to ${actionName} the request. Please try again.`;

  const handleConfirm = async () => {
    if (!onConfirm) { setOpen(false); return; }
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
      setOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { currentAssignee?: string } } };
      const status = axiosErr?.response?.status;
      if (status === 409) {
        const takenBy = axiosErr?.response?.data?.currentAssignee;
        setError(conflictMessage ?? (takenBy
          ? `This request has already been claimed by ${takenBy}.`
          : "This request has already been claimed by someone else."
        ));
      } else {
        setError(defaultFailureMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setError(null); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md lg:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? (actionName === "unclaim" ? "Release Claim" : "Claim Application Request")}</DialogTitle>
          <DialogDescription>
            {description ?? (
              actionName === "unclaim"
                ? "Please confirm you want to release this application request back to the queue."
                : "Please confirm you want to claim this application request. It will be assigned to you and removed from the available queue."
            )}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="text-sm text-rose-600 font-medium">{error}</p>
        )}
        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline" disabled={loading}>Cancel</Button>
          </DialogClose>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Loader2 className="size-3.5 animate-spin mr-1" />}
            {confirmLabel ?? (actionName === "unclaim" ? "Confirm Unclaim" : "Confirm Claim")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
